import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import type { GateInfo, UnlockResult } from "../data/useManifest";
import "./GatePage.css";

// Web-access gate (Samuel + Iris PLANS/WEB_READER_GATE_SPEC.md): a shared album
// requires the 6-char code before the reader loads. The link scopes to the
// album; the code (shared separately) is the second factor.
const CODE_LEN = 6;
// Join-code alphabet: A-Z 2-9 minus look-alikes O/0/I/1.
const ALLOWED = /[^A-HJ-NP-Z2-9]/g;

/** Padlock glyph — the gate's privacy signature. Lavender in the fallback disc;
 *  cream in the dark "PRIVATE" pill over the cover hero. */
function LockGlyph({ tone = "accent", size = 22 }: { tone?: "accent" | "cream"; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={tone === "cream" ? "#F4F0E9" : "var(--accent)"}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.4" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

/** 6 segmented mono cells — auto-uppercase, auto-advance, paste-aware. */
function CodeCells({
  value,
  onChange,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const cells = Array.from({ length: CODE_LEN }, (_, i) => value[i] ?? "");

  const setChar = (i: number, ch: string) => {
    const clean = ch.toUpperCase().replace(ALLOWED, "").slice(0, 1);
    const arr = value.padEnd(CODE_LEN).split("");
    arr[i] = clean || " ";
    onChange(arr.join("").replace(/ /g, "").slice(0, CODE_LEN));
    if (clean && i < CODE_LEN - 1) refs.current[i + 1]?.focus();
  };

  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !cells[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData
      .getData("text")
      .toUpperCase()
      .replace(ALLOWED, "")
      .slice(0, CODE_LEN);
    onChange(text);
    refs.current[Math.min(text.length, CODE_LEN - 1)]?.focus();
  };

  return (
    <div className={`gate__cells ${invalid ? "is-invalid" : ""}`} role="group" aria-label="Album code">
      {cells.map((ch, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className="gate__cell"
          value={ch}
          onChange={(e) => setChar(i, e.target.value)}
          onKeyDown={(e) => onKey(i, e)}
          onPaste={onPaste}
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={1}
          aria-label={`Character ${i + 1}`}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}

export function GatePage({
  gate,
  onUnlock,
}: {
  gate: GateInfo;
  onUnlock: (code: string) => Promise<UnlockResult>;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<null | "wrong" | "rate_limited" | "error">(null);
  const [imgFailed, setImgFailed] = useState(false);
  const canSubmit = code.length >= CODE_LEN && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    const result = await onUnlock(code);
    // "ok" -> the parent dissolves into the reader; nothing to do here.
    if (result !== "ok") {
      setBusy(false);
      setErr(result);
      if (result === "wrong") setCode("");
    }
  }

  // Cover-forward when the gate carries a public book-jacket; else the lock-disc
  // fallback (older backend / cover-render failure) — the gate always works.
  const showCover = !!gate.cover && !imgFailed;
  const headline = gate.sender
    ? `${gate.sender} shared this album with you.`
    : "You’ve been invited to a private album.";

  const form = (
    <form
      className="gate__form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <CodeCells value={code} onChange={(v) => { setErr(null); setCode(v); }} invalid={err === "wrong"} />
      {err && (
        <p className="gate__err" role="alert">
          {err === "rate_limited"
            ? "Too many tries — give it a minute and try again."
            : err === "wrong"
              ? "That code doesn’t match — check with the sender."
              : "Something went wrong — please try again."}
        </p>
      )}
      <button type="submit" className="btn btn--primary gate__submit" disabled={!canSubmit}>
        {busy ? "Checking…" : "View album"}
      </button>
    </form>
  );
  const foot = <p className="gate__foot">Only people with the code can view.</p>;

  if (showCover) {
    return (
      <div className="gate">
        <div className="gate__card gate__card--cover">
          {/* Public book-jacket hero: only the cover (photo + baked title) is
              public; contents stay code-gated — the PRIVATE pill says so. */}
          <div className="gate__hero">
            <img
              src={gate.cover!}
              alt=""
              className="gate__hero-img"
              onError={() => setImgFailed(true)}
            />
            <span className="gate__private">
              <LockGlyph tone="cream" size={13} />
              PRIVATE
            </span>
          </div>
          <div className="gate__body">
            <h1 className="gate__title">{headline}</h1>
            <p className="gate__sub">Enter the code to open it.</p>
            {form}
            {foot}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gate">
      <div className="gate__card">
        <div className="gate__lock" aria-hidden="true">
          <LockGlyph />
        </div>
        <p className="label gate__eyebrow">A private album</p>
        <h1 className="gate__title">{headline}</h1>
        <p className="gate__sub">Enter the code to open it.</p>
        {form}
        {foot}
      </div>
    </div>
  );
}

/** Dead-token card (404 / 410): a private link with no valid album — never show
 *  a code prompt for a link that can't be unlocked. */
export function GateDeadCard({ kind }: { kind: "notfound" | "revoked" }) {
  return (
    <div className="gate">
      <div className="gate__card gate__card--dead">
        <p className="label gate__eyebrow">Minnea</p>
        <h1 className="gate__title">This link isn’t available</h1>
        <p className="gate__sub">
          {kind === "revoked"
            ? "This album is no longer shared."
            : "This album link isn’t valid, or it’s no longer shared."}
        </p>
      </div>
    </div>
  );
}
